import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  CircularProgress
} from '@mui/material';
import { Warning as WarningIcon, PersonOff as DeactivateIcon, Delete as DeleteIcon } from '@mui/icons-material';

const ClientActionDialog = ({
  open,
  onClose,
  onConfirm,
  client,
  loading = false
}) => {
  const [actionType, setActionType] = useState('deactivate');

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm(actionType);
    }
  };

  const handleClose = () => {
    setActionType('deactivate');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        color: 'error.main',
        fontWeight: 600
      }}>
        <WarningIcon />
        Διαχείριση Πελάτη
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Επιλέξτε την ενέργεια που θέλετε να εκτελέσετε για τον πελάτη:
        </Typography>
        
        {client && (
          <Box sx={{ 
            p: 2, 
            backgroundColor: 'grey.50',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'grey.200',
            mb: 2
          }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Πελάτης:
            </Typography>
            <Typography variant="body1" fontWeight={500}>
              {client.name}
            </Typography>
          </Box>
        )}

        <FormControl component="fieldset" sx={{ width: '100%' }}>
          <FormLabel component="legend">Επιλέξτε ενέργεια:</FormLabel>
          <RadioGroup
            value={actionType}
            onChange={(e) => setActionType(e.target.value)}
            sx={{ mt: 1 }}
          >
            <FormControlLabel
              value="deactivate"
              control={<Radio />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DeactivateIcon fontSize="small" color="warning" />
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      Απενεργοποίηση Πελάτη
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Ο πελάτης θα γίνει ανενεργός και τα ενεργά έργα του θα απενεργοποιηθούν
                    </Typography>
                  </Box>
                </Box>
              }
            />
            <FormControlLabel
              value="delete"
              control={<Radio />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DeleteIcon fontSize="small" color="error" />
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      Οριστική Διαγραφή
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Θα διαγραφούν ο πελάτης και όλα τα σχετικά έργα, υπηρεσίες, προσφορές και τιμολόγια
                    </Typography>
                  </Box>
                </Box>
              }
            />
          </RadioGroup>
        </FormControl>

        {actionType === 'delete' && (
          <Alert severity="error" sx={{ mt: 2 }}>
            <Typography variant="body2" fontWeight={500}>
              ⚠️ Προσοχή: Αυτή η ενέργεια δεν μπορεί να αναιρεθεί!
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Θα διαγραφούν οριστικά:
              <br />• Όλα τα έργα του πελάτη
              <br />• Όλες οι υπηρεσίες
              <br />• Όλες οι προσφορές
              <br />• Όλα τα τιμολόγια
              <br />• Όλα τα σχετικά αρχεία
            </Typography>
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button 
          onClick={handleClose}
          disabled={loading}
          variant="outlined"
        >
          Ακύρωση
        </Button>
        <Button 
          onClick={handleConfirm}
          disabled={loading}
          variant="contained"
          color={actionType === 'delete' ? 'error' : 'warning'}
          startIcon={loading && <CircularProgress size={16} />}
        >
          {loading ? 'Επεξεργασία...' : 
           actionType === 'delete' ? 'Οριστική Διαγραφή' : 'Απενεργοποίηση'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClientActionDialog;
